import { useCallback, useEffect, useMemo, useState } from "react";
import * as io from "io-ts";
import createGlobalState from "@ui/lib/createGlobalState";
import { indexBy } from "diagnostics-lib/iterable";
import { notUndefined } from "diagnostics-lib/typescript";
import { generateFaultEventPayload } from "diagnostics-domain/faultEvent";
import type { Event } from "../../../api/useEvents";

type EventPublishPayload =
  | {
      type: "DIAGNOSED";
      shouldPersist: boolean;
      faultEventId: string;
      faultEventPayload: ReturnType<typeof generateFaultEventPayload>;
    }
  | {
      type: "MANUAL";
      shouldPersist: boolean;
      manualFaultEventId: string;
    };

const useFilteredCommentEvents = createGlobalState({
  storage: "session",
  key: "filteredCommentEvents",
  type: io.array(io.string),
  scoped: true,
});

export const useCommentEventAssociation = ({
  scope,
  events,
  initialEventId,
  hasEventAccess,
}: {
  scope: string;
  events: Event[];
  initialEventId: string | null;
  hasEventAccess: boolean;
}) => {
  const getEventId = useCallback(
    (event: Event) => (event.type === "DIAGNOSED" ? event.id : event.id),
    [],
  );

  const initialEvent = useMemo(
    () => events.find(event => getEventId(event) === initialEventId) ?? null,
    [events, getEventId, initialEventId],
  );

  const [selectedEventId, setSelectedEventId] = useState<string | null>(initialEventId);

  const selectedEvent = useMemo(
    () => events.find(event => getEventId(event) === selectedEventId) ?? null,
    [events, getEventId, selectedEventId],
  );

  const resetSelection = useCallback(() => {
    setSelectedEventId(initialEventId ?? null);
  }, [initialEventId]);

  const toggleSelection = useCallback(
    (id: string | null) => {
      setSelectedEventId(prev => (prev === id ? null : id));
    },
    [setSelectedEventId],
  );

  const [filteredEventOptionHash, setFilteredEventOptionHash] = useFilteredCommentEvents({
    scope,
    defaultValue: initialEvent ? [getEventId(initialEvent)] : [],
  });

  const eventIndex = useMemo(() => {
    const allEvents = initialEvent ? [initialEvent, ...events] : events;
    return indexBy(allEvents, getEventId);
  }, [events, getEventId, initialEvent]);

  const eventOptions = useMemo(
    () =>
      [...eventIndex.entries()].map(([id, event]) => ({
        value: id,
        label: event.name,
      })),
    [eventIndex],
  );

  const filtered = useMemo(
    () => filteredEventOptionHash.map(id => eventIndex.get(id)).filter(notUndefined),
    [eventIndex, filteredEventOptionHash],
  );

  useEffect(() => {
    if (!hasEventAccess) {
      setSelectedEventId(null);
    }
  }, [hasEventAccess]);

  const buildEventPublishPayload = useCallback(
    (parentEvent?: Event | null): EventPublishPayload | null => {
      const eventToPublish = parentEvent ?? selectedEvent;
      if (!eventToPublish) return null;

      if (eventToPublish.type === "MANUAL") {
        return {
          type: "MANUAL",
          shouldPersist: false /** note: already persisted */,
          manualFaultEventId: eventToPublish.id,
        };
      }

      return {
        type: "DIAGNOSED",
        faultEventPayload: generateFaultEventPayload({
          faultEvent: eventToPublish,
        }),
        shouldPersist: eventToPublish.createdAt === null,
        faultEventId: eventToPublish.id,
      };
    },
    [selectedEvent],
  );

  return {
    selection: {
      id: selectedEventId,
      event: selectedEvent,
      setId: setSelectedEventId,
      toggleId: toggleSelection,
      reset: resetSelection,
    },
    filters: {
      values: filteredEventOptionHash,
      setValues: setFilteredEventOptionHash,
      options: eventOptions,
      filteredEvents: filtered,
      hasActiveFilters: filteredEventOptionHash.length > 0,
    },
    buildEventPublishPayload,
  } as const;
};
